/**
 * ALLROUND462 global SALE countdown controller.
 *
 * Product recommendations can be injected into the DOM after the initial page
 * load. Inline <script> tags inside dynamically inserted recommendation cards
 * are not guaranteed to execute, so SALE badges must be initialized globally.
 *
 * This controller scans all current badges once per second. That means it works
 * for:
 * - homepage product cards
 * - collection cards
 * - dynamically loaded product recommendations
 * - any future cards inserted by Shopify without a page refresh
 */

(() => {
  const BERLIN_TIME_ZONE = 'Europe/Berlin';
  const DEFAULT_CYCLE_HOURS = 72;

  /**
   * Pads a numeric time segment to two digits.
   *
   * @param {number} value
   * @returns {string}
   */
  const pad = (value) => String(value).padStart(2, '0');

  /**
   * Formatter used to read calendar fields in Europe/Berlin regardless of the
   * visitor's own local timezone. This also follows German DST automatically.
   */
  const berlinFormatter = new Intl.DateTimeFormat('en-GB', {
    timeZone: BERLIN_TIME_ZONE,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    weekday: 'short',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hourCycle: 'h23',
  });

  /**
   * Returns Berlin calendar fields for the supplied Date.
   *
   * @param {Date} date
   * @returns {{
   *   year: number,
   *   month: number,
   *   day: number,
   *   weekday: string,
   *   hour: number,
   *   minute: number,
   *   second: number
   * }}
   */
  const getBerlinParts = (date) => {
    const parts = Object.fromEntries(
      berlinFormatter
        .formatToParts(date)
        .filter((part) => part.type !== 'literal')
        .map((part) => [part.type, part.value])
    );

    return {
      year: Number(parts.year),
      month: Number(parts.month),
      day: Number(parts.day),
      weekday: parts.weekday,
      hour: Number(parts.hour),
      minute: Number(parts.minute),
      second: Number(parts.second),
    };
  };

  /**
   * Converts a calendar date intended for Europe/Berlin into a timestamp.
   * Two correction passes account for CET/CEST and DST transitions.
   *
   * @returns {number}
   */
  const berlinDateToTimestamp = (year, month, day, hour, minute, second) => {
    const desiredAsUtc = Date.UTC(year, month - 1, day, hour, minute, second);
    let timestamp = desiredAsUtc;

    for (let pass = 0; pass < 2; pass += 1) {
      const actual = getBerlinParts(new Date(timestamp));
      const actualAsUtc = Date.UTC(
        actual.year,
        actual.month - 1,
        actual.day,
        actual.hour,
        actual.minute,
        actual.second
      );

      timestamp += desiredAsUtc - actualAsUtc;
    }

    return timestamp;
  };

  const weekdayNumber = {
    Sun: 0,
    Mon: 1,
    Tue: 2,
    Wed: 3,
    Thu: 4,
    Fri: 5,
    Sat: 6,
  };

  /**
   * Finds the next Wednesday or Sunday at 23:59:59 in Europe/Berlin.
   *
   * @param {Date} now
   * @returns {number}
   */
  const getNextWeeklySaleEnd = (now) => {
    const berlinNow = getBerlinParts(now);
    const currentWeekday = weekdayNumber[berlinNow.weekday];

    if (!Number.isInteger(currentWeekday)) return Number.NaN;

    const candidates = [3, 0].map((targetWeekday) => {
      const daysAhead = (targetWeekday - currentWeekday + 7) % 7;
      const targetDate = new Date(
        Date.UTC(
          berlinNow.year,
          berlinNow.month - 1,
          berlinNow.day + daysAhead
        )
      );

      return berlinDateToTimestamp(
        targetDate.getUTCFullYear(),
        targetDate.getUTCMonth() + 1,
        targetDate.getUTCDate(),
        23,
        59,
        59
      );
    });

    const futureCandidates = candidates.filter(
      (timestamp) => Number.isFinite(timestamp) && timestamp > now.getTime()
    );

    if (futureCandidates.length === 0) {
      return Math.min(
        ...candidates.map((timestamp) => timestamp + 7 * 86400000)
      );
    }

    return Math.min(...futureCandidates);
  };

  /**
   * Updates the Premium Hoodie weekly countdown badge.
   *
   * @param {HTMLElement} badge
   * @param {Date} now
   */
  const updateWeeklyBadge = (badge, now) => {
    const timer = badge.querySelector('[data-ar462-weekly-sale-time]');

    if (!(timer instanceof HTMLElement)) return;

    const saleEnd = getNextWeeklySaleEnd(now);

    if (!Number.isFinite(saleEnd)) {
      badge.hidden = true;
      return;
    }

    badge.hidden = false;

    const remainingSeconds = Math.max(
      0,
      Math.floor((saleEnd - now.getTime()) / 1000)
    );

    const days = Math.floor(remainingSeconds / 86400);
    const hours = Math.floor((remainingSeconds % 86400) / 3600);
    const minutes = Math.floor((remainingSeconds % 3600) / 60);
    const seconds = remainingSeconds % 60;

    timer.textContent =
      days > 0
        ? `${days}T ${pad(hours)}:${pad(minutes)}:${pad(seconds)}`
        : `${pad(hours)}:${pad(minutes)}:${pad(seconds)}`;

    badge.setAttribute(
      'aria-label',
      `Sale endet in ${days} Tagen, ${hours} Stunden, ${minutes} Minuten und ${seconds} Sekunden`
    );
  };

  /**
   * Updates the Zipper recurring 72-hour countdown badge.
   *
   * @param {HTMLElement} badge
   * @param {number} nowMs
   */
  const updateCycleBadge = (badge, nowMs) => {
    const timer = badge.querySelector('[data-ar462-sale-time]');
    const cycleAnchor = Date.parse(badge.dataset.saleCycleAnchor || '');
    const cycleHours = Number(
      badge.dataset.saleCycleHours || DEFAULT_CYCLE_HOURS
    );
    const cycleMs = cycleHours * 60 * 60 * 1000;

    if (
      !(timer instanceof HTMLElement) ||
      Number.isNaN(cycleAnchor) ||
      !Number.isFinite(cycleMs) ||
      cycleMs <= 0
    ) {
      badge.hidden = true;
      return;
    }

    badge.hidden = false;

    const elapsedMs = Math.max(0, nowMs - cycleAnchor);
    const elapsedInCycle = elapsedMs % cycleMs;
    const remainingMs =
      elapsedInCycle === 0 && elapsedMs > 0
        ? cycleMs
        : cycleMs - elapsedInCycle;

    const totalMinutes = Math.ceil(remainingMs / 60000);
    const hours = Math.floor(totalMinutes / 60);
    const minutes = totalMinutes % 60;

    timer.textContent = `${pad(hours)}:${pad(minutes)}`;

    badge.setAttribute(
      'aria-label',
      `Sale-Zyklus: noch ${hours} Stunden und ${minutes} Minuten`
    );
  };

  /**
   * Scans the live DOM every tick. This intentionally does not cache badge
   * references because Shopify may replace or inject product-card DOM nodes.
   */
  const updateAllSaleTimers = () => {
    const now = new Date();
    const nowMs = now.getTime();

    for (const badge of document.querySelectorAll(
      '[data-ar462-weekly-sale-countdown]'
    )) {
      if (badge instanceof HTMLElement) {
        updateWeeklyBadge(badge, now);
      }
    }

    for (const badge of document.querySelectorAll(
      '[data-ar462-sale-countdown]'
    )) {
      if (badge instanceof HTMLElement) {
        updateCycleBadge(badge, nowMs);
      }
    }
  };

  updateAllSaleTimers();
  window.setInterval(updateAllSaleTimers, 1000);
})();
